import { z } from 'zod';

import { accountTypeSchema, adminQuotaConfigSchema } from './quota';
import { companyIdSchema } from './company';

export const emailSchema = z.email({ error: 'Adresse email invalide.' });

export const passwordSchema = z.string().min(8, {
  error: 'Le mot de passe doit contenir au moins 8 caractères.',
});

export const loginPasswordSchema = z.string().min(1, {
  error: 'Le mot de passe est requis.',
});

export const loginSchema = z.object({
  email: emailSchema,
  password: loginPasswordSchema,
});

export const registerSchema = z.object({
  email: emailSchema,
  name: z.string().min(2, { error: 'Le nom doit contenir au moins 2 caractères.' }).max(100, {
    error: 'Le nom doit contenir au maximum 100 caractères.',
  }),
  password: passwordSchema,
});

export const userRoleSchema = z.enum(['admin', 'user']);

export const demoExpirationDateSchema = z.iso.datetime({
  error: 'La date d’expiration du compte démo est invalide.',
});

export const adminCreateUserBaseSchema = z.object({
  accountType: accountTypeSchema.default('subscriber'),
  companyId: companyIdSchema,
  demoExpiresAt: demoExpirationDateSchema.optional(),
  email: emailSchema,
  name: z.string().min(2, { error: 'Le nom doit contenir au moins 2 caractères.' }).max(100, {
    error: 'Le nom doit contenir au maximum 100 caractères.',
  }),
  // Rôle dans l'entreprise de rattachement ; absent = member (admin si super admin).
  organizationRole: z.enum(['admin', 'member'], {
    error: 'Le rôle dans l’entreprise est invalide.',
  }).optional(),
  quota: adminQuotaConfigSchema.optional(),
  role: userRoleSchema.default('user'),
});

export function validateDemoExpirationRequirement(
  data: Pick<z.infer<typeof adminCreateUserBaseSchema>, 'accountType' | 'demoExpiresAt'>,
  ctx: z.RefinementCtx,
) {
  if (data.accountType === 'demo' && data.demoExpiresAt === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: 'La date d’expiration est requise pour un compte démo.',
      path: ['demoExpiresAt'],
    });
  }
}

export function validateDemoRoleRequirement(
  data: Pick<z.infer<typeof adminCreateUserBaseSchema>, 'accountType' | 'role'>,
  ctx: z.RefinementCtx,
) {
  if (data.accountType === 'demo' && data.role === 'admin') {
    ctx.addIssue({
      code: 'custom',
      message: 'Le rôle super admin est réservé aux comptes abonnés.',
      path: ['role'],
    });
  }
}

export const adminCreateUserSchema = adminCreateUserBaseSchema.superRefine((data, ctx) => {
  validateDemoExpirationRequirement(data, ctx);
  validateDemoRoleRequirement(data, ctx);
});

/**
 * Réactivation / mise à jour de l'expiration d'un compte démo par le super
 * admin : une nouvelle date, ou null pour lever l'expiration.
 */
export const adminUpdateDemoExpirationSchema = z.object({
  demoExpiresAt: demoExpirationDateSchema.nullable(),
}).strict();

export const adminCompanySchema = z.object({
  id: companyIdSchema,
  name: z.string().trim().min(1, {
    error: 'Le nom de company est requis.',
  }).max(120, {
    error: 'Le nom de company est trop long.',
  }),
  slug: z.string().trim().min(1, {
    error: 'Le slug de company est requis.',
  }).max(140, {
    error: 'Le slug de company est trop long.',
  }),
});

export const adminCreateCompanySchema = z.object({
  name: z.string().trim().min(2, {
    error: 'Le nom de company doit contenir au moins 2 caractères.',
  }).max(120, {
    error: 'Le nom de company est trop long.',
  }),
});

export const adminChangeUserRoleSchema = z.object({
  role: userRoleSchema,
  userId: z.string().min(1, {
    error: 'L identifiant utilisateur est requis.',
  }),
});

export const adminSendActivationEmailSchema = z.object({
  callbackURL: z.url({
    error: 'L URL de retour est invalide.',
  }),
  email: emailSchema,
});

/**
 * Admin-only email availability check. The email is normalized like Better
 * Auth does on creation (lowercase) and trimmed so case/spacing variants of
 * an existing account are reported as taken.
 */
export const emailAvailabilityRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(emailSchema),
}).strict();

export const emailAvailabilityResponseSchema = z.object({
  data: z.object({
    available: z.boolean(),
  }),
  success: z.literal(true),
});

export const requestPasswordResetSchema = z.object({
  email: emailSchema,
  redirectTo: z.url({
    error: 'L URL de retour est invalide.',
  }).optional(),
});

export const resetPasswordSchema = z.object({
  newPassword: passwordSchema,
});

export const updateProfileSchema = z.object({
  image: z.string().trim().max(2048, {
    error: 'L URL de l avatar est trop longue.',
  }).superRefine((value, ctx) => {
    if (!value) {
      return;
    }

    const result = z.url({ error: 'L URL de l avatar est invalide.' }).safeParse(value);

    if (!result.success) {
      ctx.addIssue({
        code: 'custom',
        message: 'L URL de l avatar est invalide.',
      });
    }
  }).transform((value) => value || null).optional(),
  name: z.string().min(2, { error: 'Le nom doit contenir au moins 2 caractères.' }).max(100, {
    error: 'Le nom doit contenir au maximum 100 caractères.',
  }),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, {
    error: 'Le mot de passe actuel est requis.',
  }),
  newPassword: passwordSchema,
  revokeOtherSessions: z.boolean().default(true),
});

export type LoginDTO = z.infer<typeof loginSchema>;
export type RegisterDTO = z.infer<typeof registerSchema>;
export type AdminCreateUserDTO = z.infer<typeof adminCreateUserSchema>;
export type AdminCompanyDTO = z.infer<typeof adminCompanySchema>;
export type AdminCreateCompanyDTO = z.infer<typeof adminCreateCompanySchema>;
export type AdminChangeUserRoleDTO = z.infer<typeof adminChangeUserRoleSchema>;
export type AdminUpdateDemoExpirationDTO = z.infer<typeof adminUpdateDemoExpirationSchema>;
export type AdminSendActivationEmailDTO = z.infer<typeof adminSendActivationEmailSchema>;
export type EmailAvailabilityRequestDTO = z.infer<typeof emailAvailabilityRequestSchema>;
export type EmailAvailabilityResponseDTO = z.infer<typeof emailAvailabilityResponseSchema>;
export type RequestPasswordResetDTO = z.infer<typeof requestPasswordResetSchema>;
export type ResetPasswordDTO = z.infer<typeof resetPasswordSchema>;
export type UpdateProfileDTO = z.infer<typeof updateProfileSchema>;
export type ChangePasswordDTO = z.infer<typeof changePasswordSchema>;
